import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Collapse,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import MobileDialog from '../dialogs/MobileDialog';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import API from '../../services/api';

const fetchProductInsights = async () => {
  const response = await API.get('/products/insights/', {
    params: { ts: new Date().getTime() },
  });
  return response.data;
};

const HIGHLIGHT_OPTIONS = [
  { key: 'low_stock', label: 'מלאי נמוך', color: '#fff3e0' },
  { key: 'out_of_stock', label: 'אזל מהמלאי', color: '#ffebee' },
];

// ==================== Settings Dialog ====================
const InsightsSettingsDialog = ({ open, onClose, currentConfig, onSave }) => {
  const [threshold, setThreshold] = useState(currentConfig.low_stock_threshold || '');
  const [highlightCategories, setHighlightCategories] = useState(
    currentConfig.row_highlight_categories || ['low_stock', 'out_of_stock']
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleToggleCategory = (key) => {
    setHighlightCategories((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSave = async () => {
    if (threshold < 1) {
      setError('סף מלאי חייב להיות לפחות 1');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await API.post('/products/insights/config/', {
        low_stock_threshold: Number(threshold),
        row_highlight_categories: highlightCategories,
      });
      onSave(response.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="xs" fullWidth dir="rtl">
      <DialogTitle sx={{ fontWeight: 'bold' }}>הגדרות תובנות מלאי</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label="סף מלאי נמוך"
          type="number"
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
          onFocus={(e) => e.target.select()}
          onWheel={(e) => e.target.blur()}
          inputProps={{ min: 1, style: { direction: 'ltr' } }}
          fullWidth
          size="small"
          sx={{
            mt: 1, mb: 2,
            '& input': {
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              MozAppearance: 'textfield',
            },
          }}
          helperText="מוצרים עם מלאי מתחת לכמות זו יופיעו באזהרה ותתקבל התראה"
        />

        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
            צביעת שורות בטבלה
          </FormLabel>
          <FormGroup>
            {HIGHLIGHT_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.key}
                control={
                  <Checkbox
                    checked={highlightCategories.includes(opt.key)}
                    onChange={() => handleToggleCategory(opt.key)}
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'שמור'}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

// ==================== Insight Card ====================
const InsightCard = ({ title, icon, color, bgColor, items, renderMetric, isLoading, totalCount, onShowAll }) => (
  <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%', overflow: 'hidden', p: 0 }}>
    <Box
      dir="rtl"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        backgroundColor: bgColor,
        borderBottom: `2px solid ${color}`,
      }}
    >
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color, flex: 1, fontSize: '0.9rem', lineHeight: 1.2 }}>
        {title}
      </Typography>
      {totalCount > 0 && (
        <Chip
          label={totalCount}
          size="small"
          sx={{
            backgroundColor: color,
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '0.7rem',
            height: 20,
            minWidth: 24,
            '& .MuiChip-label': { px: 0.8 },
          }}
        />
      )}
    </Box>
    <CardContent dir="rtl" sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 }, minHeight: 100 }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
          <CircularProgress size={28} sx={{ color }} />
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          אין נתונים
        </Typography>
      ) : (
        <List dense disablePadding>
          {items.map((item, index) => (
            <ListItem
              key={item.ProductID}
              disableGutters
              sx={{
                py: 0.3,
                borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                  {item.ProductName}
                </Typography>
              </Box>
              <Chip
                label={renderMetric(item)}
                size="small"
                sx={{
                  backgroundColor: bgColor,
                  color,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  flexShrink: 0,
                  height: 22,
                  direction: 'ltr',
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
      {!isLoading && totalCount > 0 && onShowAll && (
        <Box sx={{ textAlign: 'center', pt: 0.5 }}>
          <Typography
            variant="body2"
            onClick={onShowAll}
            sx={{
              color: '#1976d2',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            הצג הכל ({totalCount})
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// ==================== Insights Config ====================
const INSIGHTS_CONFIG = [
  {
    key: 'low_stock',
    totalKey: 'low_stock_total',
    titleFn: (threshold) => `מלאי נמוך (מתחת ל-${threshold})`,
    icon: <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 20 }} />,
    color: '#ed6c02',
    bgColor: '#fff3e0',
    renderMetric: (item) => `${item.total_quantity} יחידות`,
  },
  {
    key: 'out_of_stock',
    totalKey: 'out_of_stock_total',
    title: 'אזל מהמלאי',
    icon: <RemoveShoppingCartIcon sx={{ color: '#d32f2f', fontSize: 20 }} />,
    color: '#d32f2f',
    bgColor: '#ffebee',
    renderMetric: () => 'אזל',
  },
];

// ==================== Main Panel ====================
const ProductInsightsPanel = ({
  userId,
  isManager = false,
  onHighlightIdsChange,
  onShowAll,
}) => {
  const storageKey = `products-insights-collapsed-${userId}`;
  const queryClient = useQueryClient();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['products', 'insights'],
    queryFn: fetchProductInsights,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  if (error) {
    console.error('ProductInsights API error:', error?.response?.status, error?.response?.data || error?.message);
  }

  const localThreshold = data?.low_stock_threshold ?? null;
  const isConfigured = localThreshold !== null;
  const localHighlightCategories = data?.row_highlight_categories || ['low_stock', 'out_of_stock'];

  // Pass highlight IDs to parent for row coloring
  const highlightIds = React.useMemo(() => {
    if (!data) return {};
    const ids = {};
    if (localHighlightCategories.includes('low_stock') && data.low_stock_ids) {
      ids.low_stock = new Set(data.low_stock_ids);
    }
    if (localHighlightCategories.includes('out_of_stock') && data.out_of_stock_ids) {
      ids.out_of_stock = new Set(data.out_of_stock_ids);
    }
    return ids;
  }, [data, localHighlightCategories]);

  React.useEffect(() => {
    if (onHighlightIdsChange) {
      onHighlightIdsChange(highlightIds);
    }
  }, [highlightIds, onHighlightIdsChange]);

  const handleSettingsSave = useCallback((responseData) => {
    queryClient.invalidateQueries({ queryKey: ['products', 'insights'] });
    queryClient.invalidateQueries({ queryKey: ['notification-rules'] });
  }, [queryClient]);

  const firstLoad = isLoading && !data;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        dir="rtl"
        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }} onClick={toggleCollapsed}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            תובנות מלאי
          </Typography>
          <IconButton size="small" sx={{ ml: 0.5 }}>
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
        {isManager && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setSettingsOpen(true); }}
            sx={{ color: 'text.secondary' }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Collapse in={!collapsed}>
        <Grid container spacing={1.5}>
          {INSIGHTS_CONFIG.map((cfg, index) => {
            // Low stock card: show configure prompt if threshold not set
            if (cfg.key === 'low_stock' && !isConfigured) {
              return (
                <Grid item xs={6} sm={6} md={6} lg={3} key={index}>
                  <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%', overflow: 'hidden', p: 0 }}>
                    <Box dir="rtl" sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1.5, py: 1, backgroundColor: cfg.bgColor,
                      borderBottom: `2px solid ${cfg.color}`,
                    }}>
                      {cfg.icon}
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: cfg.color, flex: 1, fontSize: '0.9rem', lineHeight: 1.2 }}>
                        מלאי נמוך
                      </Typography>
                    </Box>
                    <CardContent dir="rtl" sx={{ py: 2, px: 1.5, '&:last-child': { pb: 2 }, minHeight: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                        הגדר כמות מינימלית כדי לקבל התראות על מלאי נמוך
                      </Typography>
                      <Typography
                        variant="body2"
                        onClick={() => setSettingsOpen(true)}
                        sx={{
                          color: '#1976d2',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        הגדר עכשיו
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            }

            return (
              <Grid item xs={6} sm={6} md={6} lg={3} key={index}>
                <InsightCard
                  title={cfg.titleFn ? cfg.titleFn(localThreshold) : cfg.title}
                  icon={cfg.icon}
                  color={cfg.color}
                  bgColor={cfg.bgColor}
                  items={data?.[cfg.key] || []}
                  totalCount={data?.[cfg.totalKey] || 0}
                  renderMetric={cfg.renderMetric}
                  isLoading={firstLoad}
                  onShowAll={onShowAll ? () => onShowAll(cfg.key) : undefined}
                />
              </Grid>
            );
          })}
        </Grid>
      </Collapse>

      {settingsOpen && (
        <InsightsSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          currentConfig={{
            low_stock_threshold: localThreshold ?? '',
            row_highlight_categories: localHighlightCategories,
          }}
          onSave={handleSettingsSave}
        />
      )}
    </Box>
  );
};

export default ProductInsightsPanel;
